import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { ArrowUpRight } from "lucide-react";

const easeElegant = [0.76, 0, 0.24, 1];

const Pillar = ({ tag, title, subtitle, body, bullets, img, alt, side }) => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-10% 0px" }}
    transition={{ duration: 1.1, ease: easeElegant }}
    className={`grid md:grid-cols-12 gap-8 md:gap-12 ${
      side === "right" ? "md:pl-16 lg:pl-24" : "md:pr-16 lg:pr-24"
    } py-16 md:py-24 border-t border-white/10`}
    data-testid={`service-pillar-${tag}`}
  >
    <div className="md:col-span-5 order-2 md:order-1">
      <div className="relative aspect-[4/5] w-full overflow-hidden border border-[#C5A059]/25 group">
        <img
          src={img}
          alt={alt}
          loading="lazy"
          style={{ transitionDuration: "1400ms" }}
          className="w-full h-full object-cover transition-transform ease-out group-hover:scale-105"
        />
        <div className="absolute top-4 left-4 font-serif italic text-[#F4F4F0] text-5xl leading-none">
          {tag}
        </div>
      </div>
    </div>
    <div className="md:col-span-7 order-1 md:order-2 flex flex-col justify-center">
      <span className="overline !text-[#A1A1AA]">{subtitle}</span>
      <h3 className="mt-6 font-serif text-[#F4F4F0] text-4xl md:text-5xl lg:text-6xl font-light leading-[1] tracking-tight">
        {title}
      </h3>
      <p className="mt-8 text-[#A1A1AA] text-base md:text-lg leading-[1.65] font-light max-w-[54ch]">
        {body}
      </p>
      <ul className="mt-10 divide-y divide-white/10 border-t border-white/10">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-4 py-4">
            <span className="mt-2 block h-px w-6 bg-[#C5A059] flex-shrink-0" />
            <span className="text-[#F4F4F0]/85 text-sm md:text-base font-light leading-relaxed">
              {b}
            </span>
          </li>
        ))}
      </ul>
    </div>
  </motion.div>
);

export const Services = () => {
  const { t } = useLang();
  return (
    <section
      id="services"
      className="relative bg-[#0A0A0A] py-28 md:py-40"
      data-testid="services-section"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-24">
          <div className="md:col-span-4">
            <div className="flex items-center gap-4">
              <span className="block h-px w-12 bg-[#C5A059]" />
              <span className="overline">{t("services.overline")}</span>
            </div>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-serif text-[#F4F4F0] text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight max-w-[20ch]">
              {t("services.title")}
            </h2>
          </div>
        </div>

        {/* Pillar A */}
        <Pillar
          tag={t("services.a.tag")}
          title={t("services.a.title")}
          subtitle={t("services.a.subtitle")}
          body={t("services.a.body")}
          bullets={t("services.a.bullets") || []}
          img="https://images.unsplash.com/photo-1782500169121-b564818d1074?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxyZXN0b3JhdGlvbiUyMGludGVyaW9yJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4NDA0MTM1M3ww&ixlib=rb-4.1.0&q=85"
          alt="Restoration interior architecture"
          side="left"
        />

        {/* Pillar B */}
        <Pillar
          tag={t("services.b.tag")}
          title={t("services.b.title")}
          subtitle={t("services.b.subtitle")}
          body={t("services.b.body")}
          bullets={t("services.b.bullets") || []}
          img="https://images.unsplash.com/photo-1603033829885-bde9b5546cd5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBpdGFsaWFuJTIwdmlsbGElMjB1bWJyaWElMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg0MDQxMzUzfDA&ixlib=rb-4.1.0&q=85"
          alt="Italian architecture under sky"
          side="right"
        />

        <div className="mt-16 flex items-center justify-end gap-4">
          <a href="#contact" className="btn-gold" data-testid="services-cta">
            {t("nav.contactCta")}
            <ArrowUpRight size={16} strokeWidth={1.4} />
          </a>
        </div>
      </div>
    </section>
  );
};
